import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';

const REPORT_TYPE_OPTIONS = [
  { value: 'council', label: 'Council' },
  { value: 'executive', label: 'Executive' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
  { value: 'special', label: 'Special' },
  { value: 'other', label: 'Other' },
];

const TYPE_COLORS = {
  council: 'bg-blue-100 text-blue-700',
  executive: 'bg-purple-100 text-purple-700',
  quarterly: 'bg-green-100 text-green-700',
  annual: 'bg-indigo-100 text-indigo-700',
  special: 'bg-red-100 text-red-700',
  other: 'bg-gray-100 text-gray-700',
};

function FormInput({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
      <input {...props} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
    </div>
  );
}

export default function AdminCouncilReport() {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [form, setForm] = useState({
    title: '', report_type: 'council', meeting_date: '', description: '', is_published: false,
  });
  const [file, setFile] = useState(null);

  const fetchReports = useCallback(() => {
    setLoading(true);
    let url = '/admin/council-reports/?';
    if (search) url += `search=${encodeURIComponent(search)}&`;
    if (typeFilter) url += `report_type=${typeFilter}&`;
    api.get(url)
      .then(r => setReports(r.data.results || r.data))
      .catch(() => toast.error('Failed to load reports'))
      .finally(() => setLoading(false));
  }, [search, typeFilter]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const resetForm = () => {
    setForm({ title: '', report_type: 'council', meeting_date: '', description: '', is_published: false });
    setFile(null);
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (r) => {
    setForm({
      title: r.title, report_type: r.report_type, meeting_date: r.meeting_date || '',
      description: r.description || '', is_published: r.is_published,
    });
    setFile(null);
    setEditing(r);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('title', form.title);
    fd.append('report_type', form.report_type);
    fd.append('meeting_date', form.meeting_date);
    if (form.description) fd.append('description', form.description);
    fd.append('is_published', form.is_published);
    if (file) fd.append('file', file);

    try {
      if (editing) {
        await api.patch(`/admin/council-reports/${editing.id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Report updated');
      } else {
        await api.post('/admin/council-reports/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Report uploaded');
      }
      resetForm();
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save report');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await api.delete(`/admin/council-reports/${id}/`);
      toast.success('Report deleted');
      fetchReports();
    } catch { toast.error('Failed to delete report'); }
  };

  const togglePublish = async (r) => {
    try {
      await api.patch(`/admin/council-reports/${r.id}/`, { is_published: !r.is_published });
      toast.success(r.is_published ? 'Unpublished' : 'Published');
      fetchReports();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Council Reports</h1>
      <p className="text-sm text-gray-500 mb-6">Upload and manage council meeting reports for members to view and download</p>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" placeholder="Search reports..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E]" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E]">
          <option value="">All Types</option>
          {REPORT_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {canAdd(PERMISSIONS.ADD_COUNCIL_REPORT) && (
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition whitespace-nowrap">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Upload Report
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border p-6 mb-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">{editing ? 'Edit Report' : 'Upload Report'}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormInput label="Title *" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Report Type</label>
              <select value={form.report_type} onChange={e => setForm({ ...form, report_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                {REPORT_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <FormInput label="Meeting Date *" type="date" required value={form.meeting_date} onChange={e => setForm({ ...form, meeting_date: e.target.value })} />
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">File (PDF/DOC) {!editing && '*'}</label>
              <input type="file" accept=".pdf,.doc,.docx" onChange={e => setFile(e.target.files[0])}
                className="w-full px-3 py-2 border rounded-lg text-sm" required={!editing} />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_published} onChange={e => setForm({ ...form, is_published: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-[#0B5D1E] focus:ring-[#0B5D1E]" />
                <span className="text-sm font-medium text-gray-600">Publish immediately</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
            <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
          </div>
          <div className="flex gap-3">
            <button type="submit" className="px-6 py-2 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition">
              {editing ? 'Update' : 'Upload'}
            </button>
            <button type="button" onClick={resetForm} className="px-6 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-200 transition">Cancel</button>
          </div>
        </form>
      )}

      {/* Table */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#0B5D1E] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      ) : reports.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
          <h2 className="text-lg font-semibold text-gray-500 mb-2">No Reports</h2>
          <p className="text-gray-400">Upload your first council report.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">S/N</th>
                <th className="px-4 py-3 text-left">Title</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-center">Published</th>
                <th className="px-4 py-3 text-left">Uploaded By</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {reports.map((r, idx) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-500">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-gray-800">{r.title}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${TYPE_COLORS[r.report_type] || 'bg-gray-100 text-gray-600'}`}>
                      {r.report_type_display || r.report_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{r.meeting_date}</td>
                  <td className="px-4 py-3 text-center">
                    {canEdit(PERMISSIONS.EDIT_COUNCIL_REPORT) ? (
                      <button onClick={() => togglePublish(r)}
                        className={`text-xs px-2 py-0.5 rounded-full font-semibold cursor-pointer ${r.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {r.is_published ? 'Yes' : 'No'}
                      </button>
                    ) : (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${r.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {r.is_published ? 'Yes' : 'No'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{r.uploaded_by_name || '—'}</td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {r.file && (
                      <a href={r.file} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline text-xs">Download</a>
                    )}
                    {canEdit(PERMISSIONS.EDIT_COUNCIL_REPORT) && (
                      <button onClick={() => handleEdit(r)} className="text-blue-600 hover:underline text-xs">Edit</button>
                    )}
                    {canDelete(PERMISSIONS.DELETE_COUNCIL_REPORT) && (
                      <button onClick={() => handleDelete(r.id)} className="text-red-500 hover:underline text-xs">Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
